import React, { useEffect, useRef, useMemo, useState } from "react";
import Globe from "globe.gl";

// The visual centerpiece. Wraps globe.gl with React lifecycle.
//
// Props:
//   points : [{id, lat, lon, kind, color, severity, label, sub, ...}]
//   arcs   : [{from_lat, from_lon, to_lat, to_lon, color, ts}]
//   onSelectPoint(point)      — clicked
//   visibleKinds              — Set of kinds to show (filter)
//   warRoom                   — if true, tints globe red
//   drawerOpen                — if true, fully stop rotating while user reads
export default function ThreatGlobe({
  points = [],
  arcs = [],
  onSelectPoint,
  visibleKinds,
  warRoom = false,
  drawerOpen = false,
  flyToOnce = null,           // {lat, lon} — fires pointOfView once when set
  regionHighlight = null,     // {lat, lon} — adds an oversized cyan pulse
}) {
  const containerRef = useRef(null);
  const globeRef     = useRef(null);
  const idleTimerRef = useRef(null);
  const flewRef      = useRef(false);

  // Filter once
  const shownPoints = useMemo(
    () => (visibleKinds ? points.filter((p) => visibleKinds.has(p.kind)) : points),
    [points, visibleKinds]
  );
  const shownArcs = useMemo(
    () => (visibleKinds && !visibleKinds.has("honeypot") ? [] : arcs),
    [arcs, visibleKinds]
  );

  // Init the globe once.
  useEffect(() => {
    if (!containerRef.current) return;
    const TX_NIGHT = "/textures/earth-night-4k.jpg";    // self-hosted, 3600×1800
    const TX_BUMP  = "/textures/earth-topology-4k.png"; // self-hosted, 4K bump
    // NASA Tycho 8K Skymap — true 8192×4096 equirectangular star + Milky Way
    // panorama. Self-hosted so we don't depend on unpkg's grainy night-sky.png.
    const TX_BG    = "/textures/star-milkyway-8k.jpg";
    const g = Globe({ rendererConfig: { antialias: true, alpha: true } })(containerRef.current)
      .globeImageUrl(TX_NIGHT)
      .bumpImageUrl(TX_BUMP)
      .backgroundImageUrl(TX_BG)
      .showAtmosphere(true)
      .atmosphereColor("#00E5FF")
      .atmosphereAltitude(0.18)
      // POINTS — small bright glowing dots so the rings remain the
      // dominant pulse pattern rather than the towers obscuring the map.
      .pointAltitude((p) => 0.015 + (p.severity || 0) * 0.012)
      .pointRadius((p) => 0.32 + (p.severity || 0) * 0.06)
      .pointColor((p) => p.color)
      .pointResolution(8)
      .pointLabel((p) => {
        // Friendly label per category — mirrors the legend chips on
        // /threat-radar so the tooltip says "ACTIVELY EXPLOITED" instead
        // of the internal `kind` (which is always "news" for advisories).
        const catLabel = (
          p.kind === "quantum"
            ? (p.type === "defense" ? "Quantum Defense" : "Quantum Compute")
          : p.category === "kev"    ? "Actively Exploited"
          : p.category === "cve"    ? "CVE / NVD"
          : p.category === "vendor" ? "Supply-Chain (GHSA)"
          : p.category === "news"   ? "Cyber News"
          : (p.kind || "")
        );
        return `
        <div style="background:rgba(4,9,20,0.92);border:1px solid ${p.color};
                    color:#fff;padding:8px 10px;border-radius:6px;
                    font-family:ui-monospace,Menlo,monospace;font-size:11px;
                    max-width:240px;line-height:1.4">
          <div style="color:${p.color};font-weight:600;letter-spacing:0.06em;
                      text-transform:uppercase;font-size:10px">${escapeHtml(catLabel)}</div>
          <div style="color:#fff;margin-top:2px">${escapeHtml(p.label || "")}</div>
          <div style="color:#9aa5b8;font-size:10px;margin-top:2px">${escapeHtml(p.sub || "")}</div>
          ${p.asn ? `<div style="color:#1FDD83;font-size:10px;margin-top:2px">${escapeHtml(p.asn)}</div>` : ""}
        </div>`;
      })
      .onPointClick((p) => onSelectPoint && onSelectPoint(p))
      // RINGS — pulsing concentric circles ON the globe surface (classic
      // threat-map visual). Larger max radius + slower propagation so the
      // pulses are visible even when the camera zooms wide.
      .ringColor((r) => (t) => `${r.color}${alphaHex(1 - t)}`)
      // 40% reduction in ring travel — keeps clusters readable when zoomed wide
      .ringMaxRadius((r) => 2.7 + (r.severity || 0) * 0.72)
      .ringPropagationSpeed(1.8)
      .ringRepeatPeriod(2400)
      .ringResolution(64)
      .ringAltitude(0.005)
      // ARCS — kept for the admin honeypot trajectories
      .arcColor((a) => [a.color + "00", a.color, a.color + "00"])
      .arcStroke(0.4)
      .arcAltitudeAutoScale(0.4)
      .arcDashLength(0.4)
      .arcDashGap(2)
      .arcDashAnimateTime(2200);

    // Crisper rendering for retina + zoom-in
    try {
      const renderer = g.renderer();
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.outputColorSpace = "srgb";
    } catch (_e) { /* renderer not yet ready, skip */ }

    // Higher segment count on the globe sphere = smoother silhouette zoom
    try { g.globeMaterial().bumpScale = 8; } catch (_e) { /* mat not ready */ }

    // Auto-rotate when truly idle. The /threat-radar page is interactive
    // — the moment the user moves the mouse over the canvas we freeze the
    // globe so they can click pulses precisely.
    g.controls().autoRotate = true;
    g.controls().autoRotateSpeed = 0.35;
    g.controls().enableDamping = true;
    g.controls().dampingFactor = 0.08;
    // Wheel-zoom OFF by default so wheel events scroll the page naturally.
    // Power users can hold Shift to zoom (handled in our own wheel listener
    // below). The native control listener still fires on touch pinch.
    g.controls().enableZoom = false;

    const IDLE_RESUME_MS = 8000;
    const stopRotate = () => {
      g.controls().autoRotate = false;
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
    const scheduleResume = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        // Only resume if the drawer is still closed at that moment.
        // We re-read from the DOM rather than closure state because
        // the prop is consumed in another effect.
        if (!document.querySelector('[data-testid="tr-detail-drawer"]')) {
          g.controls().autoRotate = true;
        }
      }, IDLE_RESUME_MS);
    };
    const dom = containerRef.current;
    const onMove = () => { stopRotate(); scheduleResume(); };
    const onLeave = () => { scheduleResume(); };
    dom.addEventListener("pointermove", onMove);
    dom.addEventListener("pointerdown", onMove);
    dom.addEventListener("pointerleave", onLeave);

    // Custom wheel handler: Shift+wheel = zoom globe; plain wheel = page scroll
    const onWheel = (e) => {
      if (!e.shiftKey) return;          // let it bubble to the page
      e.preventDefault();
      e.stopPropagation();
      const cur = g.pointOfView();
      const next = Math.max(0.6, Math.min(4.0, cur.altitude + (e.deltaY > 0 ? 0.18 : -0.18)));
      g.pointOfView({ ...cur, altitude: next }, 220);
    };
    dom.addEventListener("wheel", onWheel, { passive: false });

    // Initial camera angle. Lower altitude = larger apparent globe.
    // 2.4 → 2.1 zooms us in ~12.5% so visitors don't need to scroll-zoom.
    g.pointOfView({ lat: 25, lng: -30, altitude: 2.1 }, 1500);

    globeRef.current = g;

    // Slow the apparent starfield motion by 50%.
    // The camera orbits the scene at autoRotateSpeed; the skybox is a
    // huge static sphere. Stars APPEAR to drift at the camera's angular
    // speed. Rotating the skybox in the SAME direction at half the
    // camera's speed reduces apparent motion to 50% — globe rotation is
    // untouched.
    let bgMesh = null;
    try {
      g.scene().traverse((o) => {
        if (
          o.isMesh
          && o.geometry?.type === "SphereGeometry"
          && (o.geometry.parameters?.radius || 0) > 1000
        ) {
          bgMesh = o;
        }
      });
    } catch (_e) { /* scene not yet ready */ }
    const ROT_PER_FRAME =
      (g.controls().autoRotateSpeed * 0.5 * (2 * Math.PI)) / 60 / 60;
    let rafBg = null;
    const stepBg = () => {
      if (bgMesh && g.controls().autoRotate) {
        bgMesh.rotation.y -= ROT_PER_FRAME;   // same direction as camera orbit
      }
      rafBg = requestAnimationFrame(stepBg);
    };
    rafBg = requestAnimationFrame(stepBg);

    // Resize observer — keep canvas in step with container
    const ro = new ResizeObserver(() => {
      if (!containerRef.current) return;
      g.width(containerRef.current.clientWidth);
      g.height(containerRef.current.clientHeight);
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (rafBg) cancelAnimationFrame(rafBg);
      try { dom?.removeEventListener("pointermove",  onMove); } catch (_e) { /* unmount */ }
      try { dom?.removeEventListener("pointerdown",  onMove); } catch (_e) { /* unmount */ }
      try { dom?.removeEventListener("pointerleave", onLeave); } catch (_e) { /* unmount */ }
      try { dom?.removeEventListener("wheel",        onWheel); } catch (_e) { /* unmount */ }
      try { g._destructor && g._destructor(); } catch (_e) { /* unmount */ }
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push fresh data
  useEffect(() => {
    if (!globeRef.current) return;
    // globe.gl expects {lat, lng}; our backend produces {lat, lon}.
    // Map once here instead of teaching the entire stack a new key name.
    const toGl = (p) => ({ ...p, lng: p.lng ?? p.lon });
    const ringPoints = shownPoints.filter((p) => p.kind !== "quantum").map(toGl);
    if (regionHighlight && regionHighlight.lat != null && regionHighlight.lon != null) {
      // Oversized cyan pulse so the visitor's region is impossible to miss
      // for the first ~12 seconds after the flyover lands.
      ringPoints.push({
        id:       "__visitor_region__",
        lat:      regionHighlight.lat,
        lng:      regionHighlight.lon,
        color:    "#00E5FF",
        severity: 6,    // ringMaxRadius = 2.7 + 6*0.72 = ~7.0 (≈3× normal)
      });
    }
    globeRef.current
      .pointsData(shownPoints.map(toGl))
      .ringsData(ringPoints)
      .arcsData(shownArcs.map((a) => ({
        startLat: a.from_lat, startLng: a.from_lon,
        endLat:   a.to_lat,   endLng:   a.to_lon,
        color:    a.color,
      })));
  }, [shownPoints, shownArcs, regionHighlight]);

  // War-room atmosphere swap
  useEffect(() => {
    if (!globeRef.current) return;
    globeRef.current.atmosphereColor(warRoom ? "#FF2A2A" : "#00E5FF");
  }, [warRoom]);

  // One-shot flyover to the visitor's region on first arrival.
  // Skip if we've already flown OR the user has interacted (auto-rotate
  // would have been paused). Pulses arrive from the feed effect; this
  // runs once when {lat, lng} land.
  useEffect(() => {
    if (flewRef.current) return;
    if (!globeRef.current) return;
    if (!flyToOnce || flyToOnce.lat == null || flyToOnce.lon == null) return;
    flewRef.current = true;
    globeRef.current.pointOfView(
      { lat: flyToOnce.lat, lng: flyToOnce.lon, altitude: 1.6 },
      2200,
    );
  }, [flyToOnce]);
  useEffect(() => {
    if (!globeRef.current) return;
    const ctrls = globeRef.current.controls();
    if (drawerOpen) {
      ctrls.autoRotate = false;
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    } else {
      // Resume after a short pause so the close interaction settles
      idleTimerRef.current = setTimeout(() => {
        if (globeRef.current) globeRef.current.controls().autoRotate = true;
      }, 1500);
    }
  }, [drawerOpen]);

  // Imperative + / − zoom controls used by the floating buttons.
  const zoom = (dir) => {
    if (!globeRef.current) return;
    const cur = globeRef.current.pointOfView();
    const next = Math.max(0.6, Math.min(4.0, cur.altitude + (dir === "in" ? -0.35 : 0.35)));
    globeRef.current.pointOfView({ ...cur, altitude: next }, 320);
  };

  return (
    <>
      <div
        ref={containerRef}
        data-testid="ciso-threat-globe"
        style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse at center, #04090e 0%, #000 80%)",
        }}
      />
      {/* Floating zoom controls — positioned ABOVE the Qelli avatar
          (avatar = 124px tall, anchored to the bottom-right). bottom:200
          clears it cleanly + leaves ~50px of breathing room. */}
      <div style={{ position: "absolute", right: 24, bottom: 200, zIndex: 6,
                    display: "flex", flexDirection: "column", gap: 6,
                    pointerEvents: "auto" }}>
        <button data-testid="globe-zoom-in"  onClick={() => zoom("in")}
                title="Zoom in (or Shift + scroll)"
                aria-label="Zoom globe in"
                style={zoomBtnStyle}>+</button>
        <button data-testid="globe-zoom-out" onClick={() => zoom("out")}
                title="Zoom out (or Shift + scroll)"
                aria-label="Zoom globe out"
                style={zoomBtnStyle}>−</button>
      </div>
    </>
  );
}

const zoomBtnStyle = {
  width: 40, height: 40,
  background: "rgba(4,9,20,0.85)",
  border: "1px solid rgba(0,229,255,0.45)",
  color: "#00E5FF",
  fontFamily: "ui-monospace,Menlo,monospace",
  fontSize: 20, fontWeight: 700,
  cursor: "pointer",
  borderRadius: 6,
  backdropFilter: "blur(8px)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
  display: "flex", alignItems: "center", justifyContent: "center",
  lineHeight: 1,
};

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Convert a 0-1 alpha to a 2-char hex pair the ring colour formula expects.
function alphaHex(a) {
  const v = Math.max(0, Math.min(1, a));
  return Math.round(v * 255).toString(16).padStart(2, "0");
}
